import { FallbackProvider, Web3Provider } from "@ethersproject/providers";
import * as t from "io-ts";
import { SubsetOf } from "./types";

export const providers = ["alchemy", "infura", "drpc"] as const;

export type ProviderKey = (typeof providers)[number];

type ProviderWithStatus = SubsetOf<ProviderKey, "infura" | "alchemy">;

export const STATUS_URL: Record<ProviderWithStatus, string> = {
  infura: "https://status.infura.io/history.atom",
  alchemy: "https://status.alchemy.com/history.atom",
};

export type ProviderPriority = readonly [ProviderKey, ProviderKey, ProviderKey];

interface ClientProviderBrand {
  readonly ClientProvider: unique symbol;
}

interface ServerProviderBrand {
  readonly ServerProvider: unique symbol;
}

/**
 * Functionally the same as `FallbackProvider`, but only used for reads.
 */
export type ClientProvider = t.Branded<FallbackProvider, ClientProviderBrand>;
export type ServerProvider = t.Branded<FallbackProvider, ServerProviderBrand>;

export const toClientProvider = <T extends FallbackProvider>(provider: T) => {
  return provider as unknown as ClientProvider;
};

export const toFallbackProvider = (provider: Web3Provider) => {
  return new FallbackProvider(
    [
      {
        provider,
      },
    ],
    1
  );
};

export const toServerProvider = <T extends FallbackProvider>(provider: T) => {
  return provider as unknown as ServerProvider;
};

interface SigningProviderBrand {
  readonly SigningProvider: unique symbol;
}

/**
 * Functionally the same as `Web3Provider`, but only used for transactions.
 */
export type SigningProvider = t.Branded<Web3Provider, SigningProviderBrand>;

/**
 * Casts a `Web3Provider` to a `SigningProvider`
 */
export const toSigningProvider = <T extends Web3Provider>(provider: T) => {
  return provider as unknown as SigningProvider;
};
